"use client";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { getInsuranceCardDisplayName } from "@/server/models/insurance-number/insurance-card-name";
import { formatDate } from "@/server/utils/format-date";
import { formatInsuranceNumberForDisplay } from "@/utils/format-insurance-number";
import { formatRelationshipToDisplay } from "@/utils/relationships";
import { type FC, type ReactNode } from "react";
import { InsuranceHistoryData } from "./types";

const DataRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => {
  return (
    <div className="flex min-h-14 items-center gap-8 py-2">
      <p className="w-32 shrink-0 text-sm font-bold text-monotone-9">{label}</p>
      <p>{value}</p>
    </div>
  );
};

export const InformationSheet: FC<{
  data: InsuranceHistoryData | null;
  viewerMemberId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  currentDependents?: string[];
}> = ({ data, viewerMemberId, open, onOpenChange, currentDependents = [] }) => {
  const isViewerOwnData = viewerMemberId === data?.memberId;
  const isDependentData = data?.type === "dependent";
  const isCurrentDependent =
    isDependentData && data != null
      ? currentDependents.includes(data.memberId)
      : false;
  const insuranceTypeText = isDependentData ? "被扶養者" : "被保険者本人";
  const lastSyncTime = data?.lastSyncTime;
  const [officeCode, insuranceNumber, branchNumber] = (
    data ? formatInsuranceNumberForDisplay(data.number) : ""
  ).split("-");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle className="text-xl font-bold text-monotone-9">
            {insuranceTypeText}資格情報
          </SheetTitle>
          <SheetDescription className="text-xs text-monotone-6">
            {lastSyncTime
              ? formatDate(lastSyncTime, "YYYY年M月D日 H:mm") + "時点"
              : "最新の情報を表示しています。"}
          </SheetDescription>
        </SheetHeader>
        <div className="py-6">
          <div className="divide-y divide-monotone-2">
            <DataRow
              label="氏名"
              value={
                data &&
                getInsuranceCardDisplayName({
                  insuranceCardName: data.insuranceCardName,
                  name: data.name,
                })
              }
            />
            <DataRow label="記号" value={officeCode} />
            <DataRow label="番号" value={insuranceNumber} />
            <DataRow label="枝番" value={branchNumber} />
            <DataRow label="事業所" value={data?.office?.name} />
            {isDependentData &&
              isViewerOwnData && ( // 被扶養者が自身の情報を表示する場合
                <DataRow
                  label="被保険者"
                  value={`${data.primaryMember?.name} (${data.primaryMember?.number ? formatInsuranceNumberForDisplay(data.primaryMember.number) : ""})`}
                />
              )}
            {!isViewerOwnData && ( // 被保険者が情報を表示する場合
              <DataRow label="資格種類" value={insuranceTypeText} />
            )}
            {isDependentData && ( // 被扶養者の情報を表示する場合
              <DataRow
                label="続柄"
                value={formatRelationshipToDisplay(data.relationship)}
              />
            )}
            {isViewerOwnData && ( // 被扶養者が情報を表示する場合
              <DataRow label="資格種類" value={insuranceTypeText} />
            )}
            <DataRow
              label="加入状況"
              value={data?.lostAt ? "資格喪失" : "加入中"}
            />
            {isCurrentDependent && ( // 現在の被扶養者の場合のみ表示
              <DataRow label="mykenpo登録状況" value="登録済み" />
            )}
            <DataRow
              label="資格取得日"
              value={formatDate(data?.acquiredAt, DEFAULT_DATE_FORMAT)}
            />
            <DataRow
              label="資格喪失日"
              value={
                data?.lostAt
                  ? formatDate(data?.lostAt, DEFAULT_DATE_FORMAT)
                  : "-"
              }
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
